'use client';

import { type components } from '~/fecom-api/schema';
import { Tooltipper } from '../common/tooltipper';
import { InlineLink } from '../common/inline-link';
import { useResizeObserver } from '~/hooks/use-resize-observer';
import { cn } from '~/lib/utils';

interface ProductListVariantSwatchesProps {
  variants?: components['schemas']['VariantType'][] | null;
  defaultItemCode: string;
}

export function ProductListVariantSwatches({
  variants,
  defaultItemCode,
}: Readonly<ProductListVariantSwatchesProps>) {
  const colourVariantType = variants?.[0]?.name?.includes('Colour')
    ? variants[0]
    : variants?.[1]?.name?.includes('Colour')
      ? variants[1]
      : null;

  // colourVariantType?.variantValues.forEach((v) => {
  //   v.baseColour = 'Clear'; // Temporary hack to test multiple swatch
  // });

  const swatchValues =
    colourVariantType?.variantValues?.filter((v) => v.baseColour) ?? [];

  const [containerRef, { width }] = useResizeObserver<HTMLDivElement>();

  if (!colourVariantType || swatchValues.length === 0) {
    return null;
  }

  const itemSize = 32; // Approximate size of each swatch including margin

  const containerWidth = (width || containerRef.current?.clientWidth) ?? 0;
  const totalSwatches = swatchValues.length;
  const maxSwatches = containerWidth
    ? Math.floor(containerWidth / itemSize)
    : totalSwatches; // Assuming each swatch takes approx 32px including margin
  const showPlus = swatchValues.length > maxSwatches;
  const visibleSwatches = showPlus
    ? swatchValues.slice(0, maxSwatches - 1)
    : swatchValues;

  const remainingSwatchCount = totalSwatches - visibleSwatches.length;

  // Map baseColour string to a CSS custom property value
  const getSwatchStyle = (baseColour?: string): React.CSSProperties => {
    if (!baseColour) return { background: '#eee' };
    const colourKey = baseColour.trim().toLowerCase();
    const cssVarMap: Record<string, string> = {
      red: 'var(--swatch-red)',
      yellow: 'var(--swatch-yellow)',
      pink: 'var(--swatch-pink)',
      green: 'var(--swatch-green)',
      purple: 'var(--swatch-purple)',
      orange: 'var(--swatch-orange)',
      blue: 'var(--swatch-blue)',
      black: 'var(--swatch-black)',
      white: 'var(--swatch-white)',
      grey: 'var(--swatch-grey)',
      gray: 'var(--swatch-grey)',
      brown: 'var(--swatch-brown)',
      cream: 'var(--swatch-cream)',
      clear: 'var(--swatch-clear)',
      gold: 'var(--swatch-gold)',
      silver: 'var(--swatch-silver)',
      multiple: 'var(--swatch-multiple)',
    };
    const cssVar = cssVarMap[colourKey];

    if (!cssVar) {
      return {
        backgroundColor: baseColour,
      };
    }

    if (
      colourKey === 'clear' ||
      colourKey === 'gold' ||
      colourKey === 'silver' ||
      colourKey === 'multiple'
    ) {
      return {
        backgroundImage: cssVar,
      };
    }

    return {
      backgroundColor: cssVar,
    };
  };

  const renderVariantSwatch = (baseColour?: string) => {
    const style = getSwatchStyle(baseColour);
    const isMultiple = baseColour === 'Multiple';

    return (
      <span
        style={style}
        className={cn('relative block h-full w-full rounded-full', {
          'scale-150 blur-[2px]': isMultiple,
        })}
      >
        <span className="sr-only">{baseColour}</span>
      </span>
    );
  };

  return (
    <>
      <div className={cn('relative -mx-1 -my-1 overflow-hidden px-2 py-1')}>
        <div
          ref={containerRef}
          className={cn(
            'flex w-full items-center justify-start transition-opacity',
          )}
        >
          <>
            {visibleSwatches.map((variant) => {
              const isActive = variant.isSelected ?? false;
              return (
                <div
                  key={variant.itemCode}
                  className={cn('h-6 w-8 shrink-0', {})}
                  // id there are greater than 3 swatches and we are on the first 3, hide until we know the width
                  style={{
                    opacity:
                      showPlus && maxSwatches <= 3 && !containerWidth ? 0 : 1,
                  }}
                >
                  <Tooltipper content={variant.name}>
                    <InlineLink
                      href={`/product/${variant.itemCode.toLowerCase()}`}
                      className={cn(
                        'relative block h-6 w-6 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full p-0 text-xs font-bold no-underline',
                        {
                          'ring-[1px] ring-black ring-offset-1': isActive,
                        },
                      )}
                      variant="inherit"
                      prefetch={true}
                    >
                      {renderVariantSwatch(variant.baseColour ?? undefined)}
                      {/* ring inner border - had to make separate span due to issue rendering over gradient */}
                      <span className="absolute inset-0 z-10 rounded-full border border-black/40 bg-transparent"></span>
                    </InlineLink>
                  </Tooltipper>
                </div>
              );
            })}
            {showPlus && (
              <Tooltipper key="moreOptions" content="More Options">
                <InlineLink
                  href={`/product/${defaultItemCode.toLowerCase()}`}
                  className="flex cursor-pointer items-center justify-center p-0 text-gray-500 no-underline hover:underline"
                  variant="inherit"
                  scroll={false}
                  prefetch={true}
                >
                  <span className="flex h-full w-full items-center justify-center">
                    +{remainingSwatchCount}
                  </span>
                </InlineLink>
              </Tooltipper>
            )}
          </>
        </div>
        <div
          className={cn(
            'pointer-events-none absolute inset-0 z-20 transition-opacity duration-300',
            'bg-gradient-to-r from-transparent via-white via-75% to-white',
            width === 0 ? 'opacity-100' : 'opacity-0',
          )}
        ></div>
      </div>
    </>
  );
}
